import { clearOAuthClientState } from "./authState";

const STORAGE_STATE = "oauth_state";
const STORAGE_VERIFIER = "oauth_code_verifier";
const STORAGE_BASE_URL = "oauth_gitlab_base_url";
const STORAGE_INTENT = "oauth_intent";

export type OAuthIntent = "signin" | "link";

export function getOAuthCallbackPath(): string {
  const configured = (import.meta.env.VITE_OAUTH_CALLBACK_PATH as string | undefined)?.trim();
  if (!configured) return "/oauth/callback";
  return configured.startsWith("/") ? configured : `/${configured}`;
}

export function getOAuthCallbackUrl(): string {
  return `${window.location.origin}${getOAuthCallbackPath()}`;
}

function cleanupOAuthStorage(): void {
  sessionStorage.removeItem(STORAGE_STATE);
  sessionStorage.removeItem(STORAGE_VERIFIER);
  sessionStorage.removeItem(STORAGE_BASE_URL);
  sessionStorage.removeItem(STORAGE_INTENT);
}

/**
 * Wipe any provider-side OAuth artifacts a recoverable error implies are stale.
 * Used when the backend reports an `oauth_*` recoverable code so the next
 * sign-in attempt starts from a clean slate without any user intervention.
 * The backend separately expires the `__Host-dc_session` cookie via Set-Cookie.
 */
function clearProviderOAuthArtifacts(): void {
  clearOAuthClientState();
}

export type OAuthInitiateResult =
  | { kind: "navigating" }
  | { kind: "popup_opened"; popup: Window }
  | { kind: "popup_blocked" };

/**
 * Initiate the GitLab OAuth Authorization Code + PKCE flow.
 * The backend owns state + PKCE verifier storage and returns a provider authorize URL.
 *
 * When `usePopup` is true the flow opens a popup window instead of a full
 * navigation and returns `{ kind: "popup_blocked" }` if the browser blocked it.
 */
export async function initiateOAuthFlow(
  gitlabBaseUrl: string,
  _clientId?: string,
  options: { usePopup?: boolean; intent?: OAuthIntent } = {}
): Promise<OAuthInitiateResult> {
  const { usePopup = false, intent = "signin" } = options;
  cleanupOAuthStorage();
  const params = new URLSearchParams({
    gitlab_base_url: gitlabBaseUrl,
  });
  if (intent === "link") params.set("intent", "link");
  const response = await fetch(`/api/auth/gitlab/authorize?${params.toString()}`, {
    credentials: "include",
  });
  const payload = (await response.json().catch(() => ({ error: "Unknown error" }))) as {
    authorize_url?: string;
    error?: string;
  };
  if (!response.ok || !payload.authorize_url) {
    throw new Error(payload.error ?? `Could not start GitLab sign-in (${response.status})`);
  }

  if (usePopup) {
    const popup = window.open(
      payload.authorize_url,
      "gitlab_oauth",
      "width=600,height=700,noopener,noreferrer"
    );
    if (!popup) {
      return { kind: "popup_blocked" };
    }
    return { kind: "popup_opened", popup };
  }

  window.location.href = payload.authorize_url;
  return { kind: "navigating" };
}

export type GitLabAuthResult = {
  action: "signed_in" | "link_required" | "already_connected_elsewhere" | "session_required";
  provider: "gitlab";
  connectionId?: string;
  target_email?: string;
  original_provider?: string;
  original_instance_url?: string;
  message?: string;
  linkedProvider?: string;
  linkStatus?: "completed" | "invalid";
  gitlabBaseUrl: string;
};

/**
 * Handle the OAuth redirect callback.
 * Sends the provider code + server-issued state to the backend, which validates
 * state, consumes PKCE verifier storage, exchanges tokens, and returns the
 * normalized callback payload.
 *
 * Throws on missing params or server errors.
 */
export async function completeGitLabAuthCallback(): Promise<GitLabAuthResult> {
  const params = new URL(window.location.href).searchParams;
  const code = params.get("code");
  const state = params.get("state");

  // Always clean up legacy client-side OAuth keys to avoid stale pre-server-state flows.
  cleanupOAuthStorage();

  if (!state) {
    throw new Error("OAuth callback missing state");
  }

  if (!code) {
    throw new Error("OAuth callback missing authorization code");
  }

  const response = await fetch("/api/auth/callback", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      code,
      state,
    }),
  });

  const data = (await response.json().catch(() => ({ error: "Unknown error" }))) as (
    | {
        action: "signed_in";
        provider: "gitlab";
        connectionId: string;
        linkedProvider?: string;
        linkStatus?: "completed" | "invalid";
      }
    | {
        action: "link_required";
        provider: "gitlab";
        target_email: string;
        original_provider: string;
        original_instance_url: string;
      }
    | {
        action: "already_connected_elsewhere";
        provider: "gitlab";
        message: string;
      }
    | {
        action: "session_required";
        provider: "gitlab";
        message: string;
      }
    | { error?: string; code?: string }
  ) & { gitlabBaseUrl?: string };

  if (!response.ok && !("action" in data)) {
    const message = data.error
      ? `${data.error} (${response.status})`
      : `OAuth token exchange failed: ${response.status}`;
    if (data.code) {
      // Recoverable: caller can wipe local OAuth artifacts and restart the flow.
      clearProviderOAuthArtifacts();
      const error = new Error(message) as Error & { code?: string; recoverable?: boolean };
      error.code = data.code;
      error.recoverable = true;
      throw error;
    }
    throw new Error(message);
  }
  if (!("action" in data)) {
    throw new Error(data.error ?? "OAuth token exchange failed");
  }

  return {
    action: data.action,
    provider: "gitlab",
    connectionId: "connectionId" in data ? data.connectionId : undefined,
    target_email: "target_email" in data ? data.target_email : undefined,
    original_provider: "original_provider" in data ? data.original_provider : undefined,
    original_instance_url: "original_instance_url" in data ? data.original_instance_url : undefined,
    message: "message" in data ? data.message : undefined,
    linkedProvider: "linkedProvider" in data ? data.linkedProvider : undefined,
    linkStatus: "linkStatus" in data ? data.linkStatus : undefined,
    gitlabBaseUrl:
      "gitlabBaseUrl" in data && typeof data.gitlabBaseUrl === "string"
        ? data.gitlabBaseUrl
        : "https://gitlab.com",
  };
}

export const handleOAuthCallback = completeGitLabAuthCallback;
